import { useSelector } from 'react-redux'
import { configureStore, createReducer, current, Draft, Store } from '@reduxjs/toolkit'
import { Moment } from 'moment'
import { SearchProps, SearchResultItemCategory } from '@/types/Page'
import _compact from 'lodash/compact'
import { LAST_SEARCH_KEY, SavedWizardState, SearchResult } from '@/components/ui/wizard/Wizard'
import { RatingValues } from '@/components/ui/wizard/filter/Rating'
import { LocationFilterElement } from '../../../domain-config/domain-config'
import { getTimeSearchQuery } from '@/components/ui/wizard/WizardImplementation'
import {
  resetFilters,
  resetGuestSearch,
  resetLocations,
  resetTopic,
  resetTravelPeriodSearch,
  setActiveStep,
  setEndDate,
  setFlexibleTravelPeriodSelected,
  setLocationFilter,
  setLocationSearchResult,
  setLocationSearchTerm,
  setMaximumPrice,
  setNumberOfGuests,
  setOrder,
  setRating,
  setStars,
  setSavedSearchLoaded,
  setSearchBounds,
  setSelectedLocationItems,
  setSelectedOptionCalendarFlexibility,
  setSelectedOptionDuration,
  setSelectedOptionPeriods,
  setSelectedTopicItems,
  setStartDate,
  setState,
  setTopicSearchResult,
  setTopicSearchTerm,
  setTranslate,
  toggleSelectedActionPeriods,
  resetFiltersExceptGuests,
} from '@/components/ui/wizard/WizardActions'
import { TIME_SEARCH_PARAMETERS_UPDATED } from '@/components/ui/wizard/constants'
import { Duration, Month } from '@/types/Search'
import { MapTypesLngLatBounds } from '@/components/ui/search/map-types'
import { StarsValues } from '@/components/ui/wizard/filter/Stars'
import { TRAVEL_DURATION_UPDATED } from '@/components/common/header/constants'

export function useWizardSelector<T>(selector: (state: WizardState) => T): T {
  return useSelector<WizardState, T>(selector)
}

function saveLocationAndTopic(): boolean {
  return (
    ['', '/'].includes(window.location.pathname) || window.location.pathname.startsWith('/search')
  )
}

function travelPeriodSearchTermFlexible(
  months: Month[] | undefined,
  duration: Duration | undefined,
  translate: (key: string) => string,
): string {
  if (!months && !duration) {
    return ''
  }

  function getMonths(): Month[] {
    return months || []
  }

  const monthsPart = getMonths()
    .map((period) => translate(period.name))
    .join(', ')

  return _compact([
    monthsPart,
    duration?.label ? translate(string(duration?.label)) : undefined,
  ]).join(' - ')
}

function string(s: string | undefined): string {
  return s ? s : ''
}

export interface WizardState extends SavedWizardState {
  travelPeriodSearchTerm: string
  guestSearchTerm: string
  numberOfGuests: number
  locationSearchTerm: string
  topicSearchTerm: string
  locationSearchResult: SearchResult[]
  locationSearchResultSearchTerm: string
  activeStep: number | undefined
  TopicSearchResult: SearchResult[]
  topicSearchResultSearchTerm: string
  startDate: Moment | undefined
  endDate: Moment | undefined
  rating: RatingValues | undefined
  stars: StarsValues | undefined
  locationFilter: LocationFilterElement[]
  maximumPrice: string | undefined
  order: number | undefined
  translate: (key: string) => string
  searchBounds: MapTypesLngLatBounds | undefined
  timeSearchQueryParams: Record<string, string>
  prefilled: boolean
  resetIndex: number
  savedSearchLoaded: boolean
}

const initialState: WizardState = {
  activeStep: undefined,
  selectedLocationItems: [],
  selectedTopicItems: [],
  locationSearchResult: [],
  locationSearchResultSearchTerm: '',
  topicSearchResultSearchTerm: '',
  TopicSearchResult: [],
  locationSearchTerm: '',
  topicSearchTerm: '',
  selectedOptionPeriods: undefined,
  selectedOptionDuration: undefined,
  selectedOptionCalendarFlexibility: undefined,
  travelPeriodSearchTerm: '',
  startDate: undefined,
  endDate: undefined,
  guestSearchTerm: '',
  numberOfGuests: 0,
  flexibleTravelPeriodSelected: false,
  rating: undefined,
  stars: undefined,
  locationFilter: [],
  maximumPrice: undefined,
  order: undefined,
  translate: (a: string) => a,
  searchBounds: undefined,
  timeSearchQueryParams: {},
  prefilled: false,
  resetIndex: 0,
  savedSearchLoaded: false,
}

export const MAX_NUMBER_OF_GUESTS = 4
export const MIN_NUMBER_OF_GUESTS = 0

export const DEFAULT_FILTER = {
  rating: undefined,
  stars: undefined,
  locationFilter: [],
  numberOfGuests: MIN_NUMBER_OF_GUESTS,
}

function formatCalendarFlexability(state: WizardState): string {
  if (!state.selectedOptionCalendarFlexibility) {
    return ''
  }

  return state.selectedOptionCalendarFlexibility.min < 13
    ? ` ±${state.selectedOptionCalendarFlexibility.min} ${state.translate('flexibilityFourDays')}`
    : ` ±${state.selectedOptionCalendarFlexibility.min / 7} ${state.translate('flexibilityWeeks')}`
}

function getTravelPeriodSearchTermCalender(state: WizardState): string {
  const startDate = state.startDate
  const endDate = state.endDate

  if (!startDate && !endDate) {
    return ''
  }

  return `${startDate ? startDate.locale('de').format('L') : ''} - ${
    endDate ? endDate.locale('de').format('L') : ''
  }${formatCalendarFlexability(state)} `
}

function searchStateAsJson(state: WizardState): string {
  return JSON.stringify({
    startDate: state.startDate,
    endDate: state.endDate,
    selectedOptionCalendarFlexibility: state.selectedOptionCalendarFlexibility,
    selectedOptionPeriods: state.selectedOptionPeriods,
    selectedOptionDuration: state.selectedOptionDuration,
    flexibleTravelPeriodSelected: state.flexibleTravelPeriodSelected,
    travelPeriodSearchTerm: state.travelPeriodSearchTerm,
    lastUpdated: Date.now(),
  })
}

function searchStateAsJsonWithoutDestinationAndTopic(state: WizardState): string {
  return JSON.stringify({
    selectedLocationItems: state.selectedLocationItems,
    selectedTopicItems: state.selectedTopicItems,
    startDate: state.startDate,
    endDate: state.endDate,
    selectedOptionCalendarFlexibility: state.selectedOptionCalendarFlexibility,
    selectedOptionPeriods: state.selectedOptionPeriods,
    selectedOptionDuration: state.selectedOptionDuration,
    flexibleTravelPeriodSelected: state.flexibleTravelPeriodSelected,

    travelPeriodSearchTerm: state.flexibleTravelPeriodSelected
      ? travelPeriodSearchTermFlexible(
          state.selectedOptionPeriods,
          state.selectedOptionDuration,
          state.translate,
        )
      : getTravelPeriodSearchTermCalender(state),
    lastUpdated: Date.now(),
  })
}

function saveSearchState(state: WizardState): void {
  if (typeof window === 'undefined') {
    return
  }

  if (saveLocationAndTopic()) {
    window.localStorage.setItem(LAST_SEARCH_KEY, searchStateAsJsonWithoutDestinationAndTopic(state))
    window.dispatchEvent(new Event('storage'))
  } else {
    window.localStorage.setItem(LAST_SEARCH_KEY, searchStateAsJson(state))
    window.dispatchEvent(new Event('storage'))
  }
}

function incrementResetIndex(state: Draft<WizardState>): void {
  state.resetIndex = state.resetIndex + 1
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(setSavedSearchLoaded, (state) => {
    state.savedSearchLoaded = true
  })
  builder.addCase(setTranslate, (state, action) => {
    state.translate = action.payload
  })
  builder.addCase(setSearchBounds, (state, action) => {
    state.selectedLocationItems = [
      {
        label: state.translate('destinationMapAreaLabel'),
        text: state.translate('destinationMapAreaLabel'),
        type: SearchResultItemCategory.MapArea,
        value: '',
      },
    ]
    state.searchBounds = action.payload
  })
  builder.addCase(setActiveStep, (state, action) => {
    state.activeStep = action.payload === -1 ? undefined : action.payload
    if (state.activeStep === 1) {
      state.locationSearchTerm = ''
    }
    if (state.activeStep === 2) {
      state.topicSearchTerm = ''
    }
    if (!state.startDate || !state.endDate) {
      state.startDate = undefined
      state.endDate = undefined
      state.travelPeriodSearchTerm = state.flexibleTravelPeriodSelected
        ? travelPeriodSearchTermFlexible(
            state.selectedOptionPeriods,
            state.selectedOptionDuration,
            state.translate,
          )
        : getTravelPeriodSearchTermCalender(state)
    }
  })
  builder.addCase(setSelectedLocationItems, (state, action) => {
    state.selectedLocationItems = action.payload || []
    state.selectedLocationItems = state.selectedLocationItems.filter(
      (li) => li.type !== SearchResultItemCategory.MapArea,
    )
    state.searchBounds = undefined
    saveSearchState(state)
  })
  builder.addCase(setSelectedTopicItems, (state, action) => {
    state.selectedTopicItems = action.payload || []
    saveSearchState(state)
  })
  builder.addCase(resetTopic, (state) => {
    state.selectedTopicItems = []
    state.topicSearchTerm = ''
    incrementResetIndex(state)
    saveSearchState(state)
  })
  builder.addCase(setLocationSearchResult, (state, action) => {
    state.locationSearchResult = action.payload.searchResult
    state.locationSearchResultSearchTerm = action.payload.searchTerm
  })
  builder.addCase(resetLocations, (state) => {
    state.selectedLocationItems = []
    state.locationSearchTerm = ''
    state.searchBounds = undefined
    incrementResetIndex(state)
    saveSearchState(state)
  })
  builder.addCase(setLocationSearchTerm, (state, action) => {
    state.locationSearchTerm = action.payload
  })
  builder.addCase(setTopicSearchResult, (state, action) => {
    state.TopicSearchResult = action.payload.searchResult
    state.topicSearchResultSearchTerm = action.payload.searchTerm
  })
  builder.addCase(setTopicSearchTerm, (state, action) => {
    state.topicSearchTerm = action.payload
  })
  builder.addCase(setSelectedOptionPeriods, (state, action) => {
    state.startDate = undefined
    state.endDate = undefined
    state.selectedOptionPeriods = action.payload
    state.travelPeriodSearchTerm = travelPeriodSearchTermFlexible(
      state.selectedOptionPeriods,
      state.selectedOptionDuration,
      state.translate,
    )
    saveSearchState(state)
  })
  builder.addCase(toggleSelectedActionPeriods, (state, action) => {
    if (!action.payload) {
      return
    }

    state.startDate = undefined
    state.endDate = undefined
    const month = action.payload?.month

    function monthAlreadySelected(): boolean {
      if (!month) {
        return false
      }

      return !!state.selectedOptionPeriods?.map((period) => period.month).includes(month)
    }

    if (monthAlreadySelected()) {
      state.selectedOptionPeriods = state.selectedOptionPeriods?.filter(
        (period) => period.month !== month,
      )
    } else {
      state.selectedOptionPeriods = [...(state.selectedOptionPeriods || []), action.payload]
    }

    state.travelPeriodSearchTerm = travelPeriodSearchTermFlexible(
      state.selectedOptionPeriods,
      state.selectedOptionDuration,
      state.translate,
    )
    saveSearchState(state)
  })
  builder.addCase(setSelectedOptionDuration, (state, action) => {
    state.startDate = undefined
    state.endDate = undefined
    state.selectedOptionDuration = action.payload
    state.travelPeriodSearchTerm = travelPeriodSearchTermFlexible(
      state.selectedOptionPeriods,
      state.selectedOptionDuration,
      state.translate,
    )
    saveSearchState(state)
  })
  builder.addCase(setSelectedOptionCalendarFlexibility, (state, action) => {
    state.selectedOptionCalendarFlexibility = action.payload
    state.travelPeriodSearchTerm = getTravelPeriodSearchTermCalender(state)
    saveSearchState(state)
  })
  builder.addCase(resetTravelPeriodSearch, (state) => {
    state.startDate = undefined
    state.endDate = undefined
    state.selectedOptionDuration = undefined
    state.selectedOptionPeriods = undefined
    state.travelPeriodSearchTerm = ''
    state.selectedOptionCalendarFlexibility = undefined
    saveSearchState(state)
    incrementResetIndex(state)
  })
  builder.addCase(setStartDate, (state, action) => {
    state.selectedOptionPeriods = undefined
    state.selectedOptionPeriods = undefined
    state.startDate = action.payload
    state.travelPeriodSearchTerm = getTravelPeriodSearchTermCalender(state)
    saveSearchState(state)
  })
  builder.addCase(setEndDate, (state, action) => {
    state.selectedOptionPeriods = undefined
    state.selectedOptionPeriods = undefined
    state.endDate = action.payload
    state.travelPeriodSearchTerm = getTravelPeriodSearchTermCalender(state)
    saveSearchState(state)
  })
  builder.addCase(setNumberOfGuests, (state, action) => {
    state.numberOfGuests = action.payload
    state.guestSearchTerm = `${state.numberOfGuests} ${
      state.numberOfGuests > MIN_NUMBER_OF_GUESTS
        ? state.translate('guestSearchTermPlural')
        : state.translate('guestSearchTermSingular')
    }`
  })
  builder.addCase(resetGuestSearch, (state) => {
    state.numberOfGuests = MIN_NUMBER_OF_GUESTS
    state.guestSearchTerm = ''
  })
  builder.addCase(setFlexibleTravelPeriodSelected, (state, action) => {
    state.flexibleTravelPeriodSelected = action.payload
    if (state.flexibleTravelPeriodSelected) {
      state.startDate = undefined
      state.endDate = undefined
      state.selectedOptionCalendarFlexibility = undefined
    } else {
      state.selectedOptionDuration = undefined
      state.selectedOptionPeriods = undefined
    }
    state.travelPeriodSearchTerm = state.flexibleTravelPeriodSelected
      ? travelPeriodSearchTermFlexible(
          state.selectedOptionPeriods,
          state.selectedOptionDuration,
          state.translate,
        )
      : getTravelPeriodSearchTermCalender(state)
    saveSearchState(state)
  })
  builder.addCase(setState, (state, action) => {
    state = { ...state, ...action.payload }
    if (state.flexibleTravelPeriodSelected) {
      state.travelPeriodSearchTerm = travelPeriodSearchTermFlexible(
        state.selectedOptionPeriods,
        state.selectedOptionDuration,
        state.translate,
      )
    } else {
      state.travelPeriodSearchTerm = getTravelPeriodSearchTermCalender(state)
    }
    return state
  })
  builder.addCase(setRating, (state, action) => {
    state.rating = action.payload
  })
  builder.addCase(setStars, (state, action) => {
    state.stars = action.payload
  })
  builder.addCase(setLocationFilter, (state, action) => {
    state.locationFilter = action.payload
  })
  builder.addCase(resetFilters, (state) => {
    return { ...state, ...DEFAULT_FILTER }
  })
  builder.addCase(resetFiltersExceptGuests, (state) => {
    return { ...state, ...DEFAULT_FILTER, numberOfGuests: state.numberOfGuests }
  })
  builder.addCase(setMaximumPrice, (state, action) => {
    state.maximumPrice = action.payload
  })
  builder.addCase(setOrder, (state, action) => {
    state.order = action.payload
  })
  builder.addMatcher(
    () => true,
    (state) => {
      window.dispatchEvent(
        new CustomEvent(TIME_SEARCH_PARAMETERS_UPDATED, {
          detail: getTimeSearchQuery(
            state.selectedOptionDuration,
            state.flexibleTravelPeriodSelected,
            state.selectedOptionCalendarFlexibility,
            state.selectedOptionPeriods,
            state.startDate,
            state.endDate,
          ),
        }),
      )
      const concreteState = current(state)
      window.dispatchEvent(
        new CustomEvent(TRAVEL_DURATION_UPDATED, {
          detail: {
            duration: concreteState.selectedOptionDuration?.min,
            startDate: concreteState.startDate?.format('YYYY-MM-DD'),
            endDate: concreteState.endDate?.format('YYYY-MM-DD'),
          },
        }),
      )
    },
  )
})

export function createWizardStore(props: SearchProps, translate: (key: string) => string): Store {
  if (window.store) {
    return window.store
  }

  const store = configureStore({
    reducer,
    devTools: false,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
  })
  store.dispatch(setTranslate(translate))

  store.dispatch(
    setState({
      selectedLocationItems: props.destinations || [],
      selectedTopicItems: props.topics || [],
    }),
  )

  store.dispatch(setFlexibleTravelPeriodSelected(true))

  window.store = store

  return store
}

declare global {
  interface Window {
    store: Store<WizardState> | undefined
  }
}
